import { useMemo } from 'react';

export default function useMediaSupport() {
  const recordableMediaTypes = useMemo(() => getRecordableMediaTypes(['audio']), []);
  const playableMediaTypes = useMemo(() => getPlayableMediaTypes(), []);

  const hasRecordSupport = (...variants: string[]) =>
    variants.filter((variant) => recordableMediaTypes.includes(variant));

  const hasPlaySupport = (...variants: string[]) => variants.filter((variant) => playableMediaTypes.includes(variant));

  return {
    recordableMediaTypes,
    playableMediaTypes,
    recordableMediaType: recordableMediaTypes.at(0),
    playableMediaType: playableMediaTypes.at(0),
    hasRecordSupport,
    hasPlaySupport,
  };
}

const MEDIA_TYPES: Readonly<string[]> = ['video', 'audio'];

const CONTAINERS: Readonly<string[]> = ['mp4', 'mp3', 'webm', 'mpeg', 'ogg'];

const CODECS: Readonly<string[]> = [
  'aac',
  'opus',
  'mpeg',
  'mp4a',
  'h264',
  'h.264',
  'h265',
  'h.265',
  'avc1',
  'av1',
  'vp9',
  'vp8',
  'pcm',
];

function getPlayableMediaTypes(mediaTypes = MEDIA_TYPES) {
  return mediaTypes
    .flatMap((mediaType) => [
      ...new Set(CONTAINERS.flatMap((ext) => CODECS.flatMap((codec) => [`${mediaType}/${ext};codecs=${codec}`]))),
      ...new Set(CONTAINERS.flatMap((ext) => [`${mediaType}/${ext}`])),
    ])
    .filter((variation) => {
      const media = document.createElement(variation.startsWith('audio/') ? 'audio' : 'video');
      return ['maybe', 'probably'].includes(media.canPlayType(variation));
    });
}

function getRecordableMediaTypes(mediaTypes = MEDIA_TYPES) {
  return mediaTypes
    .flatMap((mediaType) => [
      ...new Set(CONTAINERS.flatMap((ext) => CODECS.flatMap((codec) => [`${mediaType}/${ext};codecs=${codec}`]))),
      ...new Set(CONTAINERS.flatMap((ext) => [`${mediaType}/${ext}`])),
    ])
    .filter((variation) => MediaRecorder.isTypeSupported(variation));
}
